import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Stack, Button, Group, Text } from '@repletoryreach/sdk-ui';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import {
  Alert,
  EmptyState,
  KpiFilterRow,
  PageHeader,
  TableShell,
  type KpiFilterItem,
  type ViewMode
} from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { admin } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { FilterDrawer, type FilterField, type FilterValues } from '@repletoryreach/sdk-ui/data-display';
import { usePersistedState } from '../../lib/usePersistedState';
import { ActiveFilterPills, type ActiveFilterPill } from '../../components/ActiveFilterPills';
import { TableFooter } from '../../components/TableFooter';
import { useUsers } from '../../data/useUsers';
import { useTenantMap } from '../../data/useTenants';
import { RolesTable } from './components/RolesTable';
import { RolesList } from './components/RolesList';
import { InviteStaff } from './components/InviteStaff';
import { useT } from '../../i18n/I18nProvider';

type KpiKey = 'all' | 'platform' | 'tenant';

/** `GET /v1/admin/users` returns the newest 500 accounts and no total. */
const SERVER_ROW_CAP = 500;

export default function RolesPage() {
  const t = useT();
  const qc = useQueryClient();
  // The shared hook, not a second inline query: this page used to fetch and
  // cache the same payload again under a different key.
  const { data, isLoading, error, refetch, isFetching } = useUsers();
  const all = useMemo(() => data?.data ?? [], [data]);
  const { map: tenants } = useTenantMap();
  const { user: signedIn } = useAuth();

  // Per-page UI prefs.
  const [view, setView] = usePersistedState<ViewMode>('admin.roles.view', 'table');
  const [pageSize, setPageSize] = usePersistedState<number>('admin.roles.pageSize', 25);

  // Transient state.
  const [query, setQuery] = useState('');
  const [kpi, setKpi] = useState<KpiKey | null>('all');
  const [filters, setFilters] = useState<FilterValues>({});
  const [page, setPage] = useState(1);

  const debouncedQuery = useDebounce(query, 250);

  const mutate = useMutation({
    mutationFn: ({ id, scope }: { id: string; scope: 'tenant' | 'platform' }) =>
      admin.updateUserScope(id, scope),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] })
  });

  function changeScope(u: admin.AdminUser, scope: 'tenant' | 'platform') {
    const promoting = scope === 'platform';
    const who = u.email ?? u.display_name ?? t('users.unnamed');
    openConfirmModal({
      title: t(promoting ? 'roles.promote.modal.title' : 'roles.demote.modal.title', { email: who }),
      children: (
        <Stack gap="sm">
          <Text fz="sm">
            {t(promoting ? 'roles.promote.modal.body' : 'roles.demote.modal.body', { email: who })}
          </Text>
          <Text fz="sm" c="var(--rr-color-text-muted)">
            {t(promoting ? 'roles.promote.modal.effective' : 'roles.demote.modal.effective')}
          </Text>
        </Stack>
      ),
      labels: { confirm: t(promoting ? 'roles.promote' : 'roles.demote'), cancel: t('action.cancel') },
      confirmProps: { color: promoting ? 'orange' : 'red' },
      onConfirm: () => {
        mutate
          .mutateAsync({ id: u.id, scope })
          .then(() => notifySuccess(
            t(promoting ? 'roles.toast.promoted' : 'roles.toast.demoted', { email: who }),
            t('users.view.scope.toast.updatedTitle')
          ))
          .catch((e) => notifyError(
            e instanceof Error ? e.message : t('common.unknownError'),
            t('roles.toast.failedTitle')
          ));
      }
    });
  }

  const drawerActive = Object.values(filters).filter((v) => v && v !== 'all').length;
  const hasActiveFilters =
    Boolean(query.trim()) || (kpi !== null && kpi !== 'all') || drawerActive > 0;

  function clearAllFilters() {
    setQuery('');
    setKpi('all');
    setFilters({});
    setPage(1);
  }

  const kpiLabels: Record<KpiKey, string> = {
    all: t('users.kpi.all'),
    platform: t('users.kpi.platform'),
    tenant: t('users.kpi.tenant')
  };

  const counts = useMemo(() => ({
    all: all.length,
    platform: all.filter((u) => u.scope === 'platform').length,
    tenant: all.filter((u) => u.scope === 'tenant').length
  }), [all]);

  const kpiItems: KpiFilterItem[] = [
    { id: 'all',      label: t('users.kpi.all'),      value: counts.all,      tone: 'primary' },
    { id: 'platform', label: t('users.kpi.platform'), value: counts.platform, tone: 'warning' },
    { id: 'tenant',   label: t('users.kpi.tenant'),   value: counts.tenant,   tone: 'pending' }
  ];

  const filterFields: FilterField[] = useMemo(() => {
    const orgs = Array.from(new Set(all.map((u) => u.organisation_id)));
    const options = orgs
      .map((id) => ({ value: id, label: tenants.get(id)?.name ?? t('tenantName.unknownShort') }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        key: 'scope',
        label: t('users.filters.scope'),
        placeholder: t('users.filters.anyScope'),
        options: [
          { value: 'platform', label: t('users.kpi.platform') },
          { value: 'tenant', label: t('roles.filters.tenantUser') }
        ]
      },
      {
        key: 'organisation_id',
        label: t('roles.filters.organisation'),
        placeholder: t('users.filters.anyOrganisation'),
        options
      }
    ];
  }, [all, tenants, t]);

  // What is narrowing the list, one removable pill each.
  const pills: ActiveFilterPill[] = [];
  if (kpi && kpi !== 'all') pills.push({ id: 'kpi', field: t('filters.field.show'), value: kpiLabels[kpi] });
  if (query.trim()) pills.push({ id: 'search', field: t('common.search'), value: query.trim() });
  for (const field of filterFields) {
    const value = filters[field.key];
    if (!value) continue;
    const option = field.options.find((o) => o.value === value);
    pills.push({ id: field.key, field: field.label, value: option?.label ?? value });
  }

  function removePill(id: string) {
    setPage(1);
    if (id === 'kpi') { setKpi('all'); return; }
    if (id === 'search') { setQuery(''); return; }
    setFilters((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  const pillRow = <ActiveFilterPills pills={pills} onRemove={removePill} onClear={clearAllFilters} />;

  const filtered = useMemo(() => {
    let rows = all;
    if (kpi && kpi !== 'all') rows = rows.filter((u) => u.scope === kpi);
    if (filters.scope) rows = rows.filter((u) => u.scope === filters.scope);
    if (filters.organisation_id) rows = rows.filter((u) => u.organisation_id === filters.organisation_id);
    const q = debouncedQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (u) =>
          (u.email ?? '').toLowerCase().includes(q) ||
          (u.display_name ?? '').toLowerCase().includes(q) ||
          (tenants.get(u.organisation_id)?.name ?? '').toLowerCase().includes(q) ||
          u.organisation_id.toLowerCase().includes(q)
      );
    }
    return rows;
  }, [all, kpi, filters, debouncedQuery, tenants]);

  useEffect(() => {
    if (page > 1 && (page - 1) * pageSize >= filtered.length) setPage(1);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const rowProps = {
    pendingId: mutate.isPending ? mutate.variables?.id : undefined,
    currentUserId: signedIn?.id,
    onPromote: (u: admin.AdminUser) => changeScope(u, 'platform'),
    onDemote: (u: admin.AdminUser) => changeScope(u, 'tenant')
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('roles.title')}
        description={t('roles.description')}
        actions={<InviteStaff icon={<PlusIcon />} />}
      />
      <TableShell
        title={t('users.title')}
        kpis={
          <KpiFilterRow
            items={kpiItems}
            value={kpi}
            onChange={(next) => { setKpi((next ?? 'all') as KpiKey); setPage(1); }}
          />
        }
        search={query}
        onSearch={(v) => { setQuery(v); setPage(1); }}
        searchPlaceholder={t('roles.searchPlaceholder')}
        filters={
          <Group gap="sm" wrap="nowrap">
            <FilterDrawer
              triggerLabel={t('common.filters')}
              title={t('filters.drawer.title')}
              description={t('filters.drawer.description')}
              labels={{ apply: t('action.apply'), cancel: t('action.cancel'), clear: t('filters.clearAll'), any: t('filters.any') }}
              fields={filterFields}
              values={filters}
              onApply={(next) => { setFilters(next); setPage(1); }}
            />
          </Group>
        }
        view={view}
        onViewChange={setView}
        availableViews={['table', 'list']}
        loading={isLoading}
        isEmpty={filtered.length === 0}
        empty={
          <Stack gap="md" align="center">
            {pillRow}
            <EmptyState
              title={t('users.empty')}
              description={t('roles.emptyHint')}
              action={hasActiveFilters ? <Button size="xs" variant="outline" onClick={clearAllFilters}>{t('common.clearFilters')}</Button> : undefined}
            />
          </Stack>
        }
        error={
          error ? (
            <Alert color="error">
              <Stack gap="sm">
                <span>{t('users.loadError')}</span>
                <Group gap="sm">
                  <Button size="xs" variant="outline" loading={isFetching} onClick={() => void refetch()}>
                    {t('action.retry')}
                  </Button>
                </Group>
              </Stack>
            </Alert>
          ) : null
        }
      >
        {/* The pills and the footer sit inside the body rather than in the
            shell's own slots, so they land hard against the table the way the
            design has them. */}
        <Stack gap="md">
          {pillRow}
          {view === 'table' && <RolesTable records={paged} loading={isLoading} {...rowProps} />}
          {view === 'list' && <RolesList records={paged} {...rowProps} />}
          <TableFooter
            page={page}
            pageSize={pageSize}
            total={filtered.length}
            capped={all.length >= SERVER_ROW_CAP}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        </Stack>
      </TableShell>
      {/* The most consequential change on this page still writes no audit row,
          which the operator should read here rather than discover later. */}
      <Text role="caption" c="var(--rr-color-text-muted)">{t('roles.auditNote')}</Text>
    </Stack>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <line x1="7" y1="2.5" x2="7" y2="11.5" />
      <line x1="2.5" y1="7" x2="11.5" y2="7" />
    </svg>
  );
}
